import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Check, Loader2, X } from "lucide-react";
import { Btn, Section } from "@/components/account/Shell";
import { Cell, Dash, Filters, IconBtn, PillRow, Sheet, Tag, Tiles, Two, Txt } from "@/components/account/Sheet";
import api, { formatApiError } from "@/lib/api";

const COLS = [
    { key: "slot", label: "Slot", sub: "day · time", w: "minmax(170px,1fr)" },
    { key: "who", label: "Who", sub: "name · shop", w: "minmax(180px,1.2fr)" },
    { key: "phone", label: "Phone", sub: "tap to call", w: "140px" },
    { key: "email", label: "Email", w: "minmax(200px,1.2fr)" },
    { key: "tier", label: "Reading", sub: "the plan they were looking at", w: "130px" },
    { key: "about", label: "About", sub: "what they wrote", w: "minmax(220px,1.8fr)" },
    { key: "status", label: "Status", w: "110px" },
    { key: "actions", label: "Actions", sub: "done · no show · cancel", w: "170px" },
];

/** Platform admin → Calls: the ten-minute setup calls booked off the white-label page. */
export default function CallsAdmin() {
    const [d, setD] = useState(null);
    const [busy, setBusy] = useState("");
    const [view, setView] = useState("upcoming");

    const load = () => api.get("/platform/calls").then(({ data }) => setD(data)).catch(() => setD({ upcoming: [], past: [] }));
    useEffect(() => { load(); }, []);

    const mark = async (id, status) => {
        setBusy(id);
        try { const { data } = await api.post(`/platform/calls/${id}`, { status }); toast.success(data.note); await load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(""); }
    };

    if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#4a4a4a]" /></div>;

    const upcoming = d.upcoming || [];
    const past = d.past || [];
    const rows = view === "upcoming" ? upcoming : past;
    const live = view === "upcoming";

    return (
        <div data-testid="calls-admin">
            <Tiles testid="calls-tiles" items={[
                ["Booked ahead", d.counts?.upcoming || 0, "calls still to make"],
                ["Today", d.counts?.today || 0, "on today's sheet"],
                ["All time", d.counts?.total || 0, "every call ever booked"],
                ["Hours", d.hours_label || "—", "ten minutes each"],
            ]} />
            <Filters testid="calls-filters">
                <PillRow label="Show" testid="calls-filter" value={view} onPick={setView}
                    choices={[{ key: "upcoming", label: "Coming up", count: upcoming.length }, { key: "past", label: "Been and gone", count: past.length }]} />
            </Filters>
            <div className="mt-3">
                <Section title={live ? "Coming up" : "Been and gone"} pad={false} testid="calls-sheet-section"
                    note={<span className="inline-flex items-center gap-1"><CalendarClock size={11} /> about three quarters of every day is shown as taken</span>}>
                    <Sheet cols={COLS} rows={rows} rowKey={(c) => c.id} testid="calls-sheet" minWidth={1320}
                        rowTestid={(c) => `call-row-${c.id}`} empty={live ? "Nothing booked yet." : "No past calls."}
                        cells={(c) => ({
                            slot: <Cell><Txt bold>{c.slot_label}</Txt></Cell>,
                            who: <Cell><Two a={c.name} b={c.shop} /></Cell>,
                            phone: <Cell><a href={`tel:${c.phone}`} className="truncate text-[12px] font-semibold text-[#1a56db] hover:underline">{c.phone}</a></Cell>,
                            email: <Cell><Txt>{c.email}</Txt></Cell>,
                            tier: <Cell>{c.tier ? <Txt>{c.tier}</Txt> : <Dash why="Didn't come from a plan card" />}</Cell>,
                            about: <Cell>{c.about ? <Txt sub title={c.about}>“{c.about}”</Txt> : <Dash why="Nothing written" />}</Cell>,
                            status: <Cell><Tag status={c.status} testid={`call-status-${c.id}`} /></Cell>,
                            actions: (
                                <Cell className="justify-end">
                                    {live ? (
                                        <>
                                            <Btn onClick={() => mark(c.id, "done")} disabled={busy === c.id} testid={`call-done-${c.id}`}><Check size={10} /> done</Btn>
                                            <Btn onClick={() => mark(c.id, "no_show")} disabled={busy === c.id} testid={`call-noshow-${c.id}`}>no show</Btn>
                                            <IconBtn onClick={() => mark(c.id, "cancelled")} disabled={busy === c.id} testid={`call-cancel-${c.id}`} title="Cancel this call" danger><X size={12} /></IconBtn>
                                        </>
                                    ) : <Dash why="Nothing to do on a past call" />}
                                </Cell>
                            ),
                        })} />
                </Section>
            </div>
            {!upcoming.length && live && <p className="sr-only" data-testid="calls-none">Nothing booked yet.</p>}
        </div>
    );
}
